import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { formatDate } from ".";

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function formatTimezone(
  date,
  tz = timezone,
  format = "yyyy-MM-dd HH:mm:ss zzz",
  options,
) {
  return formatInTimeZone(date, tz, format, options);
}

/**
 * Updates the date part of a Date object.
 * @param {Date} date - The Date object to update.
 * @param {Date} newDate - The new date as a Date object.
 * @returns {Date} The updated Date object.
 */
export const updateDate = (date, newDate) => {
  if (!newDate) return null;

  // Create a copy of the original date, or use current date if none exists
  const updatedDate = date ? new Date(date) : new Date();

  // Update only the date components while preserving time
  updatedDate.setFullYear(
    newDate.getFullYear(),
    newDate.getMonth(),
    newDate.getDate(),
  );

  return updatedDate;
};

/**
 * Updates the time portion of a date while preserving the date portion
 * Handles timezone properly for appointment scheduling
 * @param {Date} date - The date to update
 * @param {string} newTime - The new time in format "HH:MM AM/PM" or "HH:MM"
 * @returns {Date|null} Updated date with new time, or null if invalid input
 */
export const updateTime = (date, newTime) => {
  if (!date || !newTime) return null;

  try {
    // Parse time string with AM/PM format
    const [time, period] = newTime.split(" ");
    let [hours, minutes] = time.split(":");

    // Convert hours to 24-hour format
    hours = parseInt(hours);
    if (period === "PM" && hours !== 12) {
      hours += 12;
    } else if (period === "AM" && hours === 12) {
      hours = 0;
    }

    // ✅ FIX: Create new date with the intended local time
    // This ensures the time represents what the user actually wants
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    // Create a new date with the specific year, month, day, hour, minute in LOCAL timezone
    const updatedDate = new Date(
      year,
      month,
      day,
      hours,
      parseInt(minutes),
      0,
      0,
    );

    return updatedDate;
  } catch (e) {
    console.error("Error updating time:", e);
    return null;
  }
};

/**
 * Formats a Date object for display with optional AM/PM format.
 * @param {Date} date - The Date object to format.
 * @param {string} type - The type to use. 'date' for YYYY-MM-DD, 'time' for HH:MM, 'timeAMPM' for HH:MM AM/PM.
 * @returns {string} The formatted date or time.
 */
export const formatDateOrTime = (date, type) => {
  if (!date) return "";

  // Handle case where date is an object with value property
  const dateValue = date?.value || date;

  // Ensure we have a valid Date object
  const dateObj = dateValue instanceof Date ? dateValue : new Date(dateValue);

  if (isNaN(dateObj.getTime())) return "";

  try {
    if (type === "date") {
      return formatDate(dateObj);
    } else if (type === "time") {
      return format(dateObj, "HH:mm");
    } else if (type === "timeAMPM") {
      return format(dateObj, "hh:mm a");
    }
  } catch (err) {
    console.error("Error formatting date/time:", err);
    return "";
  }

  return "";
};

/**
 * Gets the start date based on the selected date and current view type
 * @param {Date} selectedDate - The currently selected date
 * @param {string} [currentView] - The current calendar view (Week, Month, Agenda, etc). Optional.
 * @returns {Date} The calculated start date for the view range, or start of day if no view specified
 */
export const getStart = (selectedDate, currentView = "") => {
  if (!selectedDate) return null;

  if (currentView.includes("Week")) {
    return startOfWeek(selectedDate);
  }
  if (currentView.includes("Month") || currentView === "Agenda") {
    return startOfMonth(selectedDate);
  }
  return startOfDay(selectedDate);
};

/**
 * Gets the end date based on the selected date and current view type
 * @param {Date} selectedDate - The currently selected date
 * @param {string} [currentView] - The current calendar view (Week, Month, Agenda, etc). Optional.
 * @returns {Date} The calculated end date for the view range, or end of day if no view specified
 */
export const getEnd = (selectedDate, currentView = "") => {
  if (!selectedDate) return null;

  if (currentView.includes("Week")) {
    return endOfWeek(selectedDate);
  }
  if (currentView.includes("Month") || currentView === "Agenda") {
    return endOfMonth(selectedDate);
  }
  return endOfDay(selectedDate);
};

/**
 * Normalizes a date range to operational UTC boundaries.
 * Start: selected start date at 07:00:00.000Z
 * End: selected end date + 1 day at 06:59:59.999Z
 *
 * @param {Date|string} startDate
 * @param {Date|string} endDate
 * @returns {{ startDate: string | null, endDate: string | null }}
 */
export const buildOperationalUtcWindow = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return { startDate: null, endDate: null };
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return { startDate: null, endDate: null };
  }

  // Use local calendar parts from selected dates and ignore incoming time-of-day.
  // This prevents accidental extra-day shifts when callers pass endOfDay values.
  const normalizedStartDate = new Date(
    Date.UTC(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
      7,
      0,
      0,
      0,
    ),
  );
  const normalizedEndDate = new Date(
    Date.UTC(
      end.getFullYear(),
      end.getMonth(),
      end.getDate() + 1,
      6,
      59,
      59,
      999,
    ),
  );

  return {
    startDate: normalizedStartDate.toISOString(),
    endDate: normalizedEndDate.toISOString(),
  };
};

/**
 * Formats the operational window start timestamp for display (UTC calendar date).
 * Aligns with invoice-style `formatDate(iso, null, true)` for Z strings from
 * {@link buildOperationalUtcWindow}.
 *
 * @param {string} startIso - ISO string (typically 07:00:00.000Z on first inclusive day)
 * @param {string} [pattern] - date-fns format pattern (default MM-dd-yyyy)
 * @returns {string}
 */
export const formatOperationalWindowStartForDisplay = (
  startIso,
  pattern = "MM-dd-yyyy",
) => {
  if (!startIso) return "";
  const d = new Date(startIso);
  if (Number.isNaN(d.getTime())) return "";
  return formatInTimeZone(d, "UTC", pattern);
};

/**
 * Formats the operational window end timestamp for display. Stored `endDate` is the
 * exclusive cutoff (day after last inclusive day at 06:59:59.999Z). This returns the
 * last inclusive calendar day in UTC so labels match the DateRangePicker selection.
 *
 * @param {string} endIso - ISO string from {@link buildOperationalUtcWindow}
 * @param {string} [pattern] - date-fns format pattern (default MM-dd-yyyy)
 * @returns {string}
 */
export const formatOperationalWindowEndForDisplay = (
  endIso,
  pattern = "MM-dd-yyyy",
) => {
  if (!endIso) return "";
  const d = new Date(endIso);
  if (Number.isNaN(d.getTime())) return "";
  d.setUTCDate(d.getUTCDate() - 1);
  return formatInTimeZone(d, "UTC", pattern);
};
